/**
 * ShowroomLeftNav
 *
 * Presentational left navigation for the Showroom experience. Config-driven
 * (US2055174) via `useFeatureConfig`: renders grouped sections whose items are
 * dispatched by their view-item `type` — `Card` (icon + label + description) or
 * `Link` (compact text + optional badge). Disabled state and its tooltip come from
 * the item's `clickAction.canExecute`.
 */

import React from 'react';
import { useDispatch } from 'react-redux';

import { setShowroomLeftNavOpen, useShowroomNavConfigSelector } from '@makemydeal/dr-sales-checkin-store';
import { NavItemType } from '@makemydeal/dr-sales-checkin-types';

import { useFeatureConfig } from '../../hooks/useFeatureConfig';
import type { SideNavViewItem } from '../../types/showroomNavTypes';
import {
    DrawerBackdrop,
    DrawerCloseButton,
    LeftNavContainer,
    NavItemButton,
    NavItemDescription,
    NavItemIcon,
    NavItemLabel,
    NavLinkItem,
    NavSection,
    SectionDivider,
    SectionTitle
} from './sideBarNav.style';
import NavItemTooltip from '../NavItemTooltip/NavItemTooltip';

const SideBarNav: React.FC = () => {
    const { sections } = useFeatureConfig();
    const { isLeftNavOpen } = useShowroomNavConfigSelector();
    const dispatch = useDispatch();

    const handleClose = () => dispatch(setShowroomLeftNavOpen(false));

    const renderItem = (item: SideNavViewItem) => {
        const disabled = !item.clickAction.canExecute;
        const onClick = () => {
            item.clickAction.execute();
            handleClose();
        };

        if (item.type === NavItemType.Link) {
            return (
                <NavItemTooltip key={item.key} item={item}>
                    <NavLinkItem
                        size="small"
                        weight="semibold"
                        $isDisabled={disabled}
                        aria-disabled={disabled || undefined}
                        onClick={() => {
                            if (disabled) {
                                return;
                            }
                            onClick();
                        }}
                        data-testid={`showroom-nav-item-${item.key}`}
                    >
                        <span>{item.label}</span>
                        {item.badge != null && <span>{`(${item.badge})`}</span>}
                    </NavLinkItem>
                </NavItemTooltip>
            );
        }

        const Icon = item.icon;
        const isActive = !!item.isActive;

        return (
            <NavItemTooltip key={item.key} item={item}>
                <NavItemButton
                    type="button"
                    $isActive={isActive}
                    $isDisabled={disabled}
                    disabled={disabled}
                    aria-current={isActive ? 'page' : undefined}
                    aria-disabled={disabled || undefined}
                    onClick={onClick}
                    data-testid={`showroom-nav-item-${item.key}`}
                >
                    <NavItemIcon $isActive={isActive}>{Icon && <Icon />}</NavItemIcon>
                    <NavItemLabel level={7} weight={isActive ? 'bold' : 'semibold'} $isActive={isActive}>
                        {item.label}
                    </NavItemLabel>
                    {item.description && <NavItemDescription size="small">{item.description}</NavItemDescription>}
                </NavItemButton>
            </NavItemTooltip>
        );
    };

    return (
        <>
            <DrawerBackdrop $isOpen={isLeftNavOpen} onClick={handleClose} data-testid="showroom-left-nav-backdrop" />
            <LeftNavContainer $isOpen={isLeftNavOpen} data-testid="showroom-left-nav" aria-label="Showroom navigation">
                <DrawerCloseButton
                    type="button"
                    aria-label="Close navigation"
                    data-testid="showroom-left-nav-close"
                    onClick={handleClose}
                >
                    &times;
                </DrawerCloseButton>
                {sections.map((section, index) => (
                    <React.Fragment key={section.key}>
                        {index > 0 && <SectionDivider />}
                        <NavSection data-testid={`showroom-nav-section-${section.key}`}>
                            <SectionTitle level={5} weight="semibold">
                                {section.title}
                            </SectionTitle>
                            {section.items.map((item) => renderItem(item))}
                        </NavSection>
                    </React.Fragment>
                ))}
            </LeftNavContainer>
        </>
    );
};

export default SideBarNav;
